/* eslint-disable react/button-has-type */
import { css } from '@emotion/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import cns from 'classnames';
import { ButtonHTMLAttributes, FC } from 'react';
import { Box, Flex } from '../Layout';
import { Icon } from '../Icon';
import { generateBaseStyles } from '../../common/theme/utils';
import { MapUiSize, MAPUI_SIZES } from '../../common/types';
import {
  LoadingSpinner,
  LOADING_SPINNER_CLASSNAME as SPINNER_CLASSNAME,
} from '../LoadingSpinner';
import styled from '@emotion/styled';
import { space, SpaceProps } from 'styled-system';

export const BUTTON_CLASSNAME = '__mapui_button';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'success'
  | 'error'
  | 'unstyled';

export type ButtonIconPosition = 'left' | 'right';

export const BUTTON_VARIANTS = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  ACCENT: 'accent',
  SUCCESS: 'success',
  ERROR: 'error',
  UNSTYLED: 'unstyled',
};

export const BUTTON_SIZES = MAPUI_SIZES;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    SpaceProps {
  className?: string;
  variant?: ButtonVariant;
  size?: MapUiSize;
  isLoading?: boolean;
  isFullWidth?: boolean;
  icon?: IconProp;
  iconPosition?: ButtonIconPosition;
}

export const getBorderColorForSpinner = (color) =>
  `${color} ${color} transparent transparent`;

const applyMainStyles =
  ({
    variant,
    size,
    isLoading,
    isFullWidth,
  }: Pick<ButtonProps, 'variant' | 'size' | 'isLoading' | 'isFullWidth'>) =>
  (theme) => {
    const defaultStyle = css(theme.button.defaultStyles);
    const sizeStyle = css(theme.button[size]);
    const variantStyle = css(theme.button[variant]);

    const styles = [
      defaultStyle,
      sizeStyle,
      variantStyle,
      ...generateBaseStyles({ isFullWidth }),
    ];

    if (isLoading) {
      // this will override the spinner border colors to match the button variant style
      styles.push(
        css({
          [`& .${SPINNER_CLASSNAME}`]: {
            borderColor: getBorderColorForSpinner(theme.button[variant].color),
          },
          [`&:hover .${SPINNER_CLASSNAME}`]: {
            borderColor: getBorderColorForSpinner(
              theme.button[variant]['&:hover, &:focus'].color ||
                theme.button[variant].color
            ),
          },
        })
      );
    }

    return css(...styles);
  };

const ButtonContainer = styled.button<ButtonProps>(
  ({ theme, variant = 'primary', size = 'md', isLoading, isFullWidth }) => ({
    ...applyMainStyles({
      variant,
      size,
      isLoading,
      isFullWidth,
    })(theme),
  }),
  ({ className }) => ({
    className: cns(BUTTON_CLASSNAME, className),
  }),
  space
);

export const Button: FC<ButtonProps> = ({
  children,
  isLoading = false,
  type = 'button',
  icon = undefined,
  iconPosition = 'left',
  ...rest
}: ButtonProps) => {
  return (
    <ButtonContainer type={type} {...rest}>
      {!isLoading && icon === undefined && children}

      {!isLoading && icon !== undefined && (
        <Flex justifyContent="center">
          {iconPosition === 'left' && (
            <Box mr={children && '6px'}>
              <Icon icon={icon} />
            </Box>
          )}

          {children && <Box>{children}</Box>}

          {iconPosition === 'right' && (
            <Box ml={children && '6px'}>
              <Icon icon={icon} />
            </Box>
          )}
        </Flex>
      )}
      {isLoading && <LoadingSpinner size="sm" />}
    </ButtonContainer>
  );
};
